import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ExpandLess, ExpandMore, MoreVert as MoreVertIcon } from '@mui/icons-material';
import { resolveNavTopicIcon } from '../utils/resolveNavTopicIcon';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  isEnterpriseAppPath,
  isNavPathActive,
  isTopicActive,
  NavTopic,
  PRIMARY_NAV_TOPICS,
  SECONDARY_NAV_TOPICS,
  SIDEBAR_WIDTH,
  filterNavTopicsByProgress,
  withoutBillUploadNav,
} from '../constants/appNavigation';
import {
  ENTERPRISE_PRIMARY_NAV_TOPICS,
  ENTERPRISE_SECONDARY_NAV_TOPICS,
} from '../constants/enterpriseNavigation';
import { useMobileNav } from '../context/MobileNavContext';
import { useNavVisibility } from '../hooks/useNavVisibility';
import { hiddenShellScrollbarSx } from '../constants/appShellLayout';
import { SIDEBAR_WORKSPACE_HEADING } from '../constants/productFocus';
import {
  SIDEBAR_WORKSPACE_BACKGROUND,
  SIDEBAR_WORKSPACE_BORDER,
  SIDEBAR_WORKSPACE_TEXT_PRIMARY,
  SIDEBAR_WORKSPACE_TEXT_SECONDARY,
} from '../theme';

/** Prevent mouse clicks from focusing nav buttons (avoids scroll-into-view jumps). */
const preventFocusScrollOnClick = (event: React.MouseEvent) => {
  event.preventDefault();
};

interface AppSidebarProps {
  desktopCollapsed?: boolean;
  onCollapseDesktopSidebar?: () => void;
}

const AppSidebar: React.FC<AppSidebarProps> = ({
  desktopCollapsed = false,
  onCollapseDesktopSidebar,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { mobileNavOpen, closeMobileNav } = useMobileNav();
  const navVisibility = useNavVisibility();
  const enterpriseMode = isEnterpriseAppPath(location.pathname);
  const primaryTopics = withoutBillUploadNav(enterpriseMode
    ? ENTERPRISE_PRIMARY_NAV_TOPICS
    : filterNavTopicsByProgress(
      PRIMARY_NAV_TOPICS,
      // DEEP-BUG-059: avoid first-paint locked subset (Home/My company/AI only) before profile hydrates.
      navVisibility.loading
        ? { hasProfile: true, hasAssessment: true, hasExportIntent: navVisibility.hasExportIntent }
        : navVisibility
    ));
  const secondaryTopics = enterpriseMode
    ? ENTERPRISE_SECONDARY_NAV_TOPICS
    : SECONDARY_NAV_TOPICS;

  const [sidebarOptionsAnchor, setSidebarOptionsAnchor] = useState<null | HTMLElement>(null);
  const [expandedTopics, setExpandedTopics] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    primaryTopics.forEach((topic) => {
      if (topic.children?.length && isTopicActive(location.pathname, location.search, topic)) {
        initial[topic.path] = true;
      }
    });
    return initial;
  });

  const workspaceListRef = useRef<HTMLUListElement>(null);
  const workspaceScrollTopRef = useRef(0);

  const syncWorkspaceScrollTop = useCallback(() => {
    const list = workspaceListRef.current;
    if (list) {
      workspaceScrollTopRef.current = list.scrollTop;
    }
  }, []);

  const primaryTopicsKey = primaryTopics
    .map((topic) => `${topic.path}:${topic.children?.map((child) => child.path).join(',') ?? ''}`)
    .join('|');

  useEffect(() => {
    // Accordion: keep only the active topic expanded on route change (DEEP-BUG-007).
    // Depend on the path key so a freshly filtered topic list does not reopen a section the user closed.
    setExpandedTopics((prev) => {
      const next: Record<string, boolean> = { ...prev };
      let changed = false;
      primaryTopics.forEach((topic) => {
        if (!topic.children?.length) {
          return;
        }
        const shouldExpand = isTopicActive(location.pathname, location.search, topic);
        if (next[topic.path] !== shouldExpand) {
          next[topic.path] = shouldExpand;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // primaryTopics is read from the render that produced primaryTopicsKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, primaryTopicsKey]);

  const isTopicExpanded = useCallback(
    (topic: NavTopic) => {
      if (Object.prototype.hasOwnProperty.call(expandedTopics, topic.path)) {
        return Boolean(expandedTopics[topic.path]);
      }
      return isTopicActive(location.pathname, location.search, topic);
    },
    [expandedTopics, location.pathname, location.search]
  );

  useLayoutEffect(() => {
    const list = workspaceListRef.current;
    if (!list) {
      return;
    }
    list.scrollTop = workspaceScrollTopRef.current;
  }, [location.pathname, location.search, expandedTopics]);

  const handleNavigate = useCallback(
    (path: string) => {
      syncWorkspaceScrollTop();
      navigate(path);
      if (!isDesktop) {
        closeMobileNav();
      }
    },
    [closeMobileNav, isDesktop, navigate, syncWorkspaceScrollTop]
  );

  const setTopicExpanded = (topic: NavTopic, expanded: boolean, collapseSiblings = false) => {
    setExpandedTopics((prev) => {
      if (expanded && collapseSiblings) {
        const next: Record<string, boolean> = { ...prev };
        primaryTopics.forEach((t) => {
          if (t.children?.length) {
            next[t.path] = t.path === topic.path;
          }
        });
        return next;
      }
      return { ...prev, [topic.path]: expanded };
    });
  };

  const toggleTopic = (topic: NavTopic) => {
    syncWorkspaceScrollTop();
    if (!topic.children?.length) {
      handleNavigate(topic.path);
      return;
    }

    const topicActive = isTopicActive(location.pathname, location.search, topic);
    const isExpanded = isTopicExpanded(topic);

    if (topicActive && isExpanded) {
      setTopicExpanded(topic, false);
      return;
    }

    setTopicExpanded(topic, true, true);

    if (!topicActive) {
      handleNavigate(topic.children[0].path);
    }
  };

  // DEEP-BUG-042: visible keyboard focus ring (MUI ButtonBase clears outline by default).
  const navItemStyles = (active: boolean) => ({
    borderRadius: 1.5,
    mx: 1,
    mb: 0.25,
    transition: 'background-color 0.2s ease',
    backgroundColor: active ? 'rgba(76, 175, 80, 0.2)' : 'transparent',
    '&:hover': {
      backgroundColor: active ? 'rgba(76, 175, 80, 0.26)' : 'rgba(255, 255, 255, 0.06)',
    },
    '&:focus': {
      outline: '2px solid #ffffff',
      outlineOffset: 2,
    },
    '&:focus:not(:focus-visible)': {
      outline: 'none',
    },
    '&:focus-visible, &.Mui-focusVisible': {
      outline: '2px solid #ffffff',
      outlineOffset: 2,
      boxShadow: '0 0 0 3px rgba(76, 175, 80, 0.65)',
      backgroundColor: active ? 'rgba(76, 175, 80, 0.28)' : 'rgba(255, 255, 255, 0.1)',
    },
  });

  const renderTopic = (topic: NavTopic) => {
    const hasChildren = Boolean(topic.children?.length);
    const topicActive = isTopicActive(location.pathname, location.search, topic);
    const isExpanded = isTopicExpanded(topic);

    return (
      <React.Fragment key={topic.path}>
        <ListItemButton
          onMouseDown={preventFocusScrollOnClick}
          onClick={() => toggleTopic(topic)}
          selected={topicActive && !hasChildren}
          sx={{
            ...navItemStyles(topicActive),
            mb: 0,
            '& .MuiListItemText-root': { my: 0 },
          }}
          aria-expanded={hasChildren ? isExpanded : undefined}
          aria-current={topicActive && !hasChildren ? 'page' : undefined}
        >
          <ListItemIcon
            sx={{
              minWidth: 36,
              color: topicActive ? 'primary.light' : SIDEBAR_WORKSPACE_TEXT_SECONDARY,
            }}
          >
            {resolveNavTopicIcon(topic.label)}
          </ListItemIcon>
          <ListItemText
            primary={topic.label}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: topicActive ? 600 : 500,
              color: topicActive ? SIDEBAR_WORKSPACE_TEXT_PRIMARY : SIDEBAR_WORKSPACE_TEXT_SECONDARY,
            }}
          />
          {hasChildren ? (
            <Box
              component="span"
              aria-hidden
              sx={{
                ml: 0.5,
                display: 'inline-flex',
                color: SIDEBAR_WORKSPACE_TEXT_SECONDARY,
                pointerEvents: 'none',
              }}
            >
              {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </Box>
          ) : null}
        </ListItemButton>

        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List dense disablePadding sx={{ pl: 2, pb: 0.5 }}>
              {topic.children!.map((child) => {
                const childActive = isNavPathActive(
                  location.pathname,
                  location.search,
                  child.path
                );
                return (
                  <ListItemButton
                    key={child.path}
                    onMouseDown={preventFocusScrollOnClick}
                    onClick={() => handleNavigate(child.path)}
                    selected={childActive}
                    sx={{
                      ...navItemStyles(childActive),
                      py: 0.5,
                      pl: 3,
                    }}
                    aria-current={childActive ? 'page' : undefined}
                  >
                    <ListItemText
                      primary={child.label}
                      primaryTypographyProps={{
                        fontSize: '0.8125rem',
                        fontWeight: childActive ? 600 : 400,
                        color: childActive ? SIDEBAR_WORKSPACE_TEXT_PRIMARY : SIDEBAR_WORKSPACE_TEXT_SECONDARY,
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const sidebarSurfaceStyles = {
    bgcolor: SIDEBAR_WORKSPACE_BACKGROUND,
    borderColor: SIDEBAR_WORKSPACE_BORDER,
  };

  const sidebarContent = (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        pt: { xs: 2.5, md: 1.5 },
        pb: 2,
        minHeight: 0,
        ...sidebarSurfaceStyles,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2.5,
          mb: 0.5,
          gap: 0.5,
          flexShrink: 0,
        }}
      >
        <Typography
          id="app-sidebar-workspace-heading"
          variant="overline"
          sx={{
            lineHeight: 1.4,
            flex: 1,
            minWidth: 0,
            fontWeight: 700,
            letterSpacing: 1.2,
            color: SIDEBAR_WORKSPACE_TEXT_SECONDARY,
          }}
        >
          {SIDEBAR_WORKSPACE_HEADING}
        </Typography>
        {isDesktop && onCollapseDesktopSidebar ? (
          <>
            <Tooltip title="Sidebar options">
              <IconButton
                size="small"
                aria-label="Sidebar options"
                onClick={(event) => setSidebarOptionsAnchor(event.currentTarget)}
                sx={{ color: SIDEBAR_WORKSPACE_TEXT_SECONDARY, flexShrink: 0, p: 0.25 }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={sidebarOptionsAnchor}
              open={Boolean(sidebarOptionsAnchor)}
              onClose={() => setSidebarOptionsAnchor(null)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem
                onClick={() => {
                  setSidebarOptionsAnchor(null);
                  onCollapseDesktopSidebar();
                }}
              >
                Collapse sidebar
              </MenuItem>
            </Menu>
          </>
        ) : null}
      </Box>
      <List
        ref={workspaceListRef}
        component="nav"
        aria-labelledby="app-sidebar-workspace-heading"
        disablePadding
        onScroll={syncWorkspaceScrollTop}
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 0, overflowAnchor: 'none', ...hiddenShellScrollbarSx }}
      >
        {primaryTopics.map((topic) => renderTopic(topic))}
      </List>

      <Divider sx={{ mx: 2, my: 0.5, flexShrink: 0, borderColor: SIDEBAR_WORKSPACE_BORDER }} />

      <Typography
        id="app-sidebar-more-heading"
        variant="overline"
        sx={{
          px: 2.5,
          mb: 0.5,
          lineHeight: 1.4,
          flexShrink: 0,
          fontWeight: 700,
          letterSpacing: 1.2,
          color: SIDEBAR_WORKSPACE_TEXT_SECONDARY,
        }}
      >
        More
      </Typography>
      <List dense disablePadding aria-labelledby="app-sidebar-more-heading" sx={{ flexShrink: 0 }}>
        {secondaryTopics.map((topic) => {
          const topicActive = isTopicActive(location.pathname, location.search, topic);
          return (
            <ListItemButton
              key={topic.path}
              onMouseDown={preventFocusScrollOnClick}
              onClick={() => handleNavigate(topic.path)}
              selected={topicActive}
              sx={{ ...navItemStyles(topicActive), py: 0.25, minHeight: 28 }}
              aria-current={topicActive ? 'page' : undefined}
            >
              <ListItemIcon
                sx={{
                  minWidth: 36,
                  color: topicActive ? 'primary.light' : SIDEBAR_WORKSPACE_TEXT_SECONDARY,
                }}
              >
                {resolveNavTopicIcon(topic.label)}
              </ListItemIcon>
              <ListItemText
                primary={topic.label}
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: topicActive ? 600 : 500,
                  color: topicActive ? SIDEBAR_WORKSPACE_TEXT_PRIMARY : SIDEBAR_WORKSPACE_TEXT_SECONDARY,
                }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );

  if (!isDesktop) {
    return (
      <Drawer
        variant="temporary"
        open={mobileNavOpen}
        onClose={closeMobileNav}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          zIndex: (theme) => theme.zIndex.modal,
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            zIndex: (theme) => theme.zIndex.modal + 1,
            ...sidebarSurfaceStyles,
          },
          '& .MuiBackdrop-root': {
            // DEEP-BUG-021: visible dimmed overlay (never negative z-index).
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
          },
        }}
      >
        <Box component="nav" aria-label="Main navigation" sx={{ height: '100%' }}>
          {sidebarContent}
        </Box>
      </Drawer>
    );
  }

  if (desktopCollapsed) {
    return null;
  }

  return (
    <Box
      component="nav"
      aria-label="Main navigation"
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        alignSelf: 'stretch',
        minHeight: 0,
        borderRight: '1px solid',
        ...sidebarSurfaceStyles,
      }}
    >
      {sidebarContent}
    </Box>
  );
};

export default AppSidebar;
